import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type {
  DatosDeEmision, Pagina, Prefactura, SimulacionDeFolio, VistaPreviaPrefactura,
} from '../api/tipos';
import { Cargando, Dialogo, Error, Faltantes, Paginacion } from '../componentes/comunes';
import { useSesion } from '../sesion';
import { ComprobanteDeLaFactura } from '../componentes/ComprobanteDeLaFactura';

/**
 * Lo que ya se armó: prefacturas y facturas.
 *
 * Son el mismo registro en dos momentos. Sin UUID es una prefactura: hay
 * archivo, no hay comprobante fiscal. Con el UUID integrado ya es factura,
 * porque Master EDI la timbró y el SAT la selló.
 *
 * La columna de estado dice eso mismo y no un adorno: de ella depende si
 * lo que se le manda al cliente sirve.
 */

function moneda(valor: number, divisa: string): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: divisa || 'MXN' });
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const NOMBRE_TIPO: Record<string, string> = {
  iguala: 'Iguala',
  correctivo: 'Correctivo',
  libre: 'Libre',
};

/**
 * La marca de que este archivo ya se bajó.
 *
 * Karin: «me gustaría que al descargar los txt visualmente me deje claro
 * que ya descargué alguno». El dato existía desde siempre —la API escribe
 * `txt_generado_en` en cada descarga— pero salía como una fecha gris
 * diciendo «generado», que es una palabra que no contesta la pregunta:
 * quien mira la lista quiere saber qué le falta mandar, no cuándo se armó
 * un archivo.
 *
 * Es de todos y no de este navegador, y eso importa: si el archivo se lo
 * llevó alguien más, lo que hay que saber es que ya salió, no que yo no
 * lo bajé. Guardarlo en el navegador haría que cada quien viera su propia
 * versión de qué se mandó.
 */
function Descargado({ cuando }: { cuando: string }) {
  return (
    <div className="conteo" style={{ color: 'var(--exito, #1a7f4b)', fontWeight: 600 }}>
      ✓ descargado {fechaCorta(cuando)}
    </div>
  );
}

export default function Prefacturas({ recarga }: { recarga?: number }) {
  const [pagina, setPagina] = useState<Pagina<Prefactura> | null>(null);
  const [numero, setNumero] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [estatus, setEstatus] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [previa, setPrevia] = useState<{ nombre: string; texto: string } | null>(null);
  const [factura, setFactura] = useState<
    { folio: string; comprobante: VistaPreviaPrefactura } | null>(null);
  const [emision, setEmision] = useState<DatosDeEmision | null>(null);
  /** La prefactura cuyo TXT se está a punto de volver a bajar. */
  const [confirmando, setConfirmando] = useState<Prefactura | null>(null);
  /** La factura que se está cancelando. */
  const [cancelando, setCancelando] = useState<Prefactura | null>(null);
  /** La prefactura que Master EDI no timbró y se va a anular. */
  const [anulando, setAnulando] = useState<Prefactura | null>(null);
  /** La prefactura cuyo folio se va a corregir. */
  const [renumerando, setRenumerando] = useState<Prefactura | null>(null);
  const { puede } = useSesion();

  /**
   * Los catálogos del SAT, una sola vez.
   *
   * El comprobante imprime «601 · General de Ley Personas Morales» y no
   * «601»: la clave sola obliga a saberse el catálogo de memoria para
   * notar que está mal, y quien revisa no se lo sabe. Si la petición
   * falla no se dice nada y el dibujo sale con las claves solas —es peor
   * de leer, pero sigue siendo la factura—; frenar la pantalla entera por
   * un catálogo de nombres sería cambiar un estorbo por un bloqueo.
   */
  useEffect(() => {
    api.get<DatosDeEmision>('/facturacion/prefacturas/emision')
      .then(setEmision)
      .catch(() => setEmision(null));
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Prefactura>>('/facturacion/prefacturas', {
      busqueda: busqueda || undefined,
      estatusUuid: estatus || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, estatus, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda, recarga]);

  /**
   * Baja el TXT y vuelve a preguntar por la lista.
   *
   * La marca de descargado la escribe la API al servir el archivo, así que
   * sin recargar el renglón se quedaría diciendo que falta bajarlo.
   */
  function descargarTxt(f: Prefactura) {
    setError(null);
    setConfirmando(null);

    void api.descargar(
      `/facturacion/prefacturas/${f.id}/txt`, undefined,
      f.txtNombre ?? `${f.folio}.txt`,
    ).then(cargar).catch(setError);
  }

  /** La factura como se imprime. Es lo que abre «Ver». */
  function verFactura(id: string, folio: string) {
    setError(null);
    api.get<VistaPreviaPrefactura>(`/facturacion/prefacturas/${id}/comprobante`)
      .then((comprobante) => setFactura({ folio, comprobante }))
      .catch(setError);
  }

  /**
   * El texto del archivo que lee Master EDI.
   *
   * Ya no es lo que abre «Ver» —Karin lo pidió así— pero sigue estando,
   * en su propio botón y más discreto. Es lo único que enseña qué recibe
   * el proceso que timbra, y el día que rechace un archivo sin decir por
   * qué, es por donde se averigua. Quitarlo habría dejado ese día sin
   * herramienta.
   */
  function verArchivo(id: string) {
    setError(null);
    api.get<{ nombre: string; texto: string }>(`/facturacion/prefacturas/${id}/vista-previa`)
      .then(setPrevia)
      .catch(setError);
  }

  return (
    <div className="panel">
      <div className="controles">
        <select value={estatus} onChange={(e) => { setEstatus(e.target.value); setNumero(1); }}>
          <option value="">Todas</option>
          <option value="pendiente">Prefacturas (sin timbrar)</option>
          <option value="integrado">Facturas (con UUID)</option>
          <option value="error">Con error de timbrado</option>
        </select>
        <input
          type="search"
          placeholder="Folio, orden de compra o receptor…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">Todavía no hay prefacturas con ese filtro.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Tipo</th>
                  <th>Receptor</th>
                  <th>Orden de compra</th>
                  <th>Expedida</th>
                  <th className="numero">Total</th>
                  <th>Estado</th>
                  <th>Archivo</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((f) => (
                  /* El renglón ya descargado pierde intensidad para que
                     lo que falta por mandar resalte solo. La lista se lee
                     buscando pendientes, no repasándola entera. */
                  <tr key={f.id} style={f.txtGeneradoEn ? { opacity: 0.55 } : undefined}>
                    <td className="mono">
                      {/* Tachado, como en el reporte: el folio existió y
                          se consumió, pero ya no se cobra. */}
                      <span style={f.cancelada || f.anulada
                        ? { textDecoration: 'line-through', opacity: 0.7 }
                        : undefined}
                      >
                        {f.folio}
                      </span>
                    </td>
                    <td>{NOMBRE_TIPO[f.tipo] ?? f.tipo}</td>
                    <td>
                      {f.receptor}
                      <div className="conteo mono">{f.receptorRfc}</div>
                    </td>
                    <td className="mono">{f.poNumero}</td>
                    <td>{fechaCorta(f.fechaExpedicion)}</td>
                    <td className="numero">{moneda(f.total, f.moneda)}</td>
                    <td>
                      {/* Cancelada manda sobre todo lo demás: una
                          cancelada timbrada sigue teniendo UUID, y
                          enseñarla como «Timbrada» invita a mandarla
                          otra vez. */}
                      {/* Anulada va primero: nunca fue comprobante, así
                          que llamarla «Prefactura» invitaría a volver a
                          mandarla, que es justo lo que no hay que hacer
                          —su folio ya lo lleva otra—. */}
                      {f.anulada
                        ? (
                          <span className="etiqueta no" title={f.motivoAnulacion ?? undefined}>
                            Anulada
                          </span>
                        )
                        : f.cancelada
                        ? <span className="etiqueta no" title={f.motivoCancelacion ?? undefined}>Cancelada</span>
                        : f.esPrefactura
                          ? <span className="etiqueta no">Prefactura</span>
                          : <span className="etiqueta si">Timbrada</span>}
                      {f.uuidFiscal && (
                        <div className="conteo mono" title={f.uuidFiscal}>
                          {f.uuidFiscal.slice(0, 8)}…
                        </div>
                      )}
                    </td>
                    <td>
                      {/* «Ver» enseña la factura. Es lo que se revisa, y
                          lo que se compara contra el PDF del cliente. */}
                      <button
                        className="enlace"
                        onClick={() => verFactura(f.id, f.folio)}
                      >
                        Ver
                      </button>
                      {/* La primera vez baja directo; la segunda pregunta.

                          Karin: «si le vuelvo a dar clic me indique que ya
                          fue descargada, osea si puedo volver a hacerlo
                          pero me indique como alerta que ya se habia
                          hecho». Avisa y no impide: volver a bajarlo es
                          legítimo —se perdió el archivo, Master EDI lo
                          rechazó, hay que mandarlo otra vez— y el riesgo
                          no es bajarlo dos veces sino **mandarlo** dos
                          veces a timbrar, que es lo que duplicaría la
                          factura del cliente.

                          Por eso el aviso dice cuándo se bajó: con la
                          fecha en la mano, quien lo lee sabe si fue él
                          hace un minuto o alguien el martes. */}
                      <button
                        className="enlace"
                        onClick={() => {
                          if (f.txtGeneradoEn) { setConfirmando(f); return; }
                          descargarTxt(f);
                        }}
                      >
                        {f.txtGeneradoEn ? 'Descargar otra vez' : 'Descargar TXT'}
                      </button>
                      {/* El TXT queda a un lado y en chico: es la
                          herramienta para cuando Master EDI rechaza un
                          archivo sin decir por qué, no lo que se mira a
                          diario. */}
                      <button
                        className="enlace conteo"
                        onClick={() => verArchivo(f.id)}
                        title="El texto del archivo que lee Master EDI"
                      >
                        Ver TXT
                      </button>
                      {f.txtGeneradoEn && <Descargado cuando={f.txtGeneradoEn} />}
                      {/* Cancelar. Karin: «hay veces que sucede».

                          Va al final y sin destacar: es la excepción, y
                          un botón rojo junto a «Descargar» se pulsa por
                          error. En una cancelada no se ofrece: cancelar
                          dos veces no significa nada. */}
                      {puede('facturacion', 'crear') && !f.cancelada && !f.anulada && (
                        <div>
                          {/* Las tres salidas, y cuál se ofrece depende de
                              una sola cosa: si tiene UUID.

                              Karin: «la que no hizo masteredi la "cancele"
                              (le quite ese folio) para hacerla nuevamente
                              y asigne un nuevo folio, y la que sí hizo,
                              solo editarla para que coincida».

                              Sin UUID son dos actos de cuadrar folios con
                              Master EDI, y son reversibles en el sentido
                              que importa: nada salió al SAT. Con UUID sólo
                              queda cancelar, que mueve un ingreso ya
                              contado. Enseñar las tres juntas sería
                              invitar a borrar un comprobante fiscal. */}
                          {f.esPrefactura ? (
                            <>
                              <button
                                className="enlace conteo"
                                onClick={() => setRenumerando(f)}
                                title="Master EDI le dio otro folio y hay que cuadrarlo"
                              >
                                Corregir folio
                              </button>
                              {' · '}
                              <button
                                className="enlace conteo"
                                onClick={() => setAnulando(f)}
                                title="Master EDI no la timbró: libera su folio"
                              >
                                Anular
                              </button>
                            </>
                          ) : (
                            <button className="enlace conteo" onClick={() => setCancelando(f)}>
                              Cancelar factura
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {/* El aviso de que ya se bajó, con el botón para hacerlo igual.

          Karin: «si puedo volver a hacerlo pero me indique como alerta que
          ya se habia hecho». La alerta es un diálogo y no un texto al lado
          porque tiene que interrumpir: un aviso que se puede no leer no
          sirve para lo que esto previene.

          Lo que previene no es bajar el archivo dos veces —eso es
          inofensivo— sino **mandarlo** dos veces a Master EDI, que timbra
          dos veces el mismo folio y le factura doble al cliente. Por eso el
          diálogo dice la fecha: con ella se sabe si fue uno hace un minuto
          o alguien el martes. */}
      {confirmando && (
        <Dialogo
          titulo={`El TXT de ${confirmando.folio} ya se descargó`}
          onCerrar={() => setConfirmando(null)}
          pie={
            <>
              <button onClick={() => setConfirmando(null)}>Cancelar</button>
              <button
                className="primario"
                onClick={() => descargarTxt(confirmando)}
              >
                Descargarlo otra vez
              </button>
            </>
          }
        >
          <div className="aviso">
            Este archivo se descargó el{' '}
            <strong>{fechaCorta(confirmando.txtGeneradoEn as string)}</strong>.
          </div>

          <p style={{ margin: 0 }}>
            Bajarlo otra vez no hace daño. Lo que no se debe hacer es
            <strong> dejarlo dos veces en la carpeta de Master EDI</strong>: el
            mismo folio se timbraría dos veces y el cliente recibiría la
            factura duplicada.
          </p>

          <p className="ayuda" style={{ margin: 0 }}>
            Si lo que necesitas es revisar qué dice el archivo, usa «Ver TXT»
            —no lo descarga ni vuelve a marcarlo—.
          </p>
        </Dialogo>
      )}

      {factura && (
        <Dialogo
          titulo={`Factura ${factura.folio}`}
          onCerrar={() => setFactura(null)}
          ancho
        >
          <ComprobanteDeLaFactura
            previa={factura.comprobante}
            divisa={factura.comprobante.comprobante.moneda}
            sat={emision?.sat}
            momento="emitida"
            uuidFiscal={factura.comprobante.uuidFiscal ?? null}
          />
        </Dialogo>
      )}

      {cancelando && (
        <CancelarFactura
          factura={cancelando}
          onCerrar={() => setCancelando(null)}
          onHecho={() => { setCancelando(null); cargar(); }}
        />
      )}

      {anulando && (
        <AnularPrefactura
          factura={anulando}
          onCerrar={() => setAnulando(null)}
          onHecho={() => { setAnulando(null); cargar(); }}
        />
      )}

      {renumerando && (
        <CorregirFolio
          factura={renumerando}
          onCerrar={() => setRenumerando(null)}
          onHecho={() => { setRenumerando(null); cargar(); }}
        />
      )}

      {previa && (
        <Dialogo titulo={previa.nombre} onCerrar={() => setPrevia(null)} ancho>
          <p className="sub">
            Esto es lo que se deja en la carpeta que lee Master EDI. Va en
            latin-1 y con fin de línea de Windows, que es lo que ese proceso
            espera.
          </p>
          <pre
            className="mono"
            style={{
              maxHeight: '60vh', overflow: 'auto', fontSize: 12,
              background: 'var(--panel-2)', padding: 12, borderRadius: 8,
            }}
          >
            {previa.texto}
          </pre>
        </Dialogo>
      )}
    </div>
  );
}

/**
 * Cancelar una factura emitida.
 *
 * Karin: «quiero ver la posibilidad de poner un boton de cancelar
 * factura, por que hay veces que sucede».
 *
 * Las dos preguntas del formulario no son trámite. La de refacturar
 * decide si el trabajo se vuelve a cobrar o se pierde; la de los cobros
 * avisa de un dinero que queda aplicado a algo que deja de existir.
 */
function CancelarFactura({ factura, onCerrar, onHecho }: {
  factura: Prefactura;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  /**
   * Sin respuesta por omisión, y es deliberado.
   *
   * Las dos son legítimas y marcar una de antemano haría que se
   * aceptara sin leer. Si se facturó mal, las órdenes tienen que
   * volver; si el cliente rechazó el cargo, tienen que quedarse fuera.
   */
  const [refacturar, setRefacturar] = useState<boolean | null>(null);
  const [aceptaCobros, setAceptaCobros] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const tieneCobros = factura.cobrado > 0;

  const faltantes: string[] = [];
  if (motivo.trim().length < 5) faltantes.push('escribir el motivo');
  if (refacturar === null) faltantes.push('decir si se va a volver a facturar');
  if (tieneCobros && !aceptaCobros) faltantes.push('confirmar lo de los cobros aplicados');

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/facturacion/prefacturas/${factura.id}/cancelar`, {
        motivo: motivo.trim(),
        seVaARefacturar: refacturar === true,
        aceptaLosCobros: aceptaCobros,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Cancelar la factura ${factura.folio}`}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cerrar</button>
          <button
            className="peligro"
            onClick={confirmar}
            disabled={faltantes.length > 0 || trabajando}
          >
            {trabajando ? 'Cancelando…' : 'Cancelar la factura'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="ayuda">
        El folio no se recupera: ya se consumió ante el SAT y la factura se
        queda en su sitio, cancelada. En los reportes sale tachada y fuera
        de los totales.
      </p>

      {tieneCobros && (
        <div className="aviso error">
          <strong>A esta factura ya se le aplicaron cobros.</strong>
          <p style={{ margin: '6px 0 0' }}>
            Cancelarla no devuelve ese dinero: queda aplicado a una factura
            que deja de existir, y hay que desaplicarlo en Cobranza. Si se
            timbró un complemento de pago, también hay que cancelarlo.
          </p>
          <label style={{ display: 'block', marginTop: 8 }}>
            <input
              type="checkbox"
              checked={aceptaCobros}
              onChange={(e) => setAceptaCobros(e.target.checked)}
            />
            {' '}Lo vi y aun así hay que cancelarla
          </label>
        </div>
      )}

      <div className="campo">
        <label>¿Se va a volver a facturar? *</label>
        <div className="controles">
          <label>
            <input
              type="radio" name="refacturar" checked={refacturar === true}
              onChange={() => setRefacturar(true)}
            />
            {' '}Sí: las órdenes vuelven a la bandeja
          </label>
          <label>
            <input
              type="radio" name="refacturar" checked={refacturar === false}
              onChange={() => setRefacturar(false)}
            />
            {' '}No: ya no se van a cobrar
          </label>
        </div>
        <span className="ayuda">
          Si se facturó mal y se va a reemitir, las órdenes tienen que volver
          o ese trabajo se queda sin cobrar. Si el cliente rechazó el cargo,
          tienen que quedarse fuera o alguien las va a refacturar por error.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="motivo-cancelacion">Motivo *</label>
        <textarea
          id="motivo-cancelacion" rows={3} value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
        <span className="ayuda">
          Queda en la auditoría y en la bitácora de cada orden. Es lo que va
          a leer quien revise esto dentro de seis meses.
        </span>
      </div>

      <Faltantes que={faltantes} />
    </Dialogo>
  );
}

/**
 * Anular una prefactura que Master EDI nunca timbró.
 *
 * Karin: «el folio 1256 no se genera y se salta al siguiente… quiero ver
 * que podemos hacer para en ese caso, la que no hizo masteredi la
 * "cancele" (le quite ese folio) para hacerla nuevamente».
 *
 * Es el diálogo más corto de la pantalla y tiene que serlo: aquí no hay
 * ninguna decisión que tomar. Cancelar pregunta si el trabajo se vuelve a
 * cobrar porque pudo perderse ante el cliente; aquí la factura no llegó a
 * existir, así que la orden siempre vuelve a la bandeja. Lo único que se
 * pide es el motivo.
 */
function AnularPrefactura({ factura, onCerrar, onHecho }: {
  factura: Prefactura;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const faltantes: string[] = [];
  if (motivo.trim().length < 5) faltantes.push('escribir el motivo');

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/facturacion/prefacturas/${factura.id}/anular`, {
        motivo: motivo.trim(),
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Anular la prefactura ${factura.folio}`}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cerrar</button>
          <button
            className="peligro"
            onClick={confirmar}
            disabled={trabajando || faltantes.length > 0}
          >
            {trabajando ? 'Anulando…' : 'Anular y liberar el folio'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="sub" style={{ marginTop: 0 }}>
        Para cuando Master EDI no la timbró. Como nunca llegó a ser
        comprobante —no tiene UUID y el SAT no sabe nada de ella—, el folio{' '}
        <strong>{factura.folio}</strong> queda libre y lo puede tomar otra
        factura.
      </p>

      <div className="aviso">
        La orden vuelve a <strong>«Por facturar»</strong> para volver a
        generarla, y entonces tomará el folio que siga.
      </div>

      <p className="ayuda" style={{ margin: 0 }}>
        Si lo que pasó es que Master EDI <em>sí</em> la timbró pero con otro
        número, esto no es lo que buscas: cierra y usa «Corregir folio».
      </p>

      <div className="campo">
        <label htmlFor="motivo-anulacion">Motivo *</label>
        <textarea
          id="motivo-anulacion" rows={3} value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Master EDI no la generó y se brincó al folio siguiente."
        />
        <span className="ayuda">
          Queda en la auditoría y en la bitácora de la orden. Un folio que
          desaparece de la serie sin explicación es una tarde de trabajo
          dentro de seis meses.
        </span>
      </div>

      <Faltantes que={faltantes} />
    </Dialogo>
  );
}

/**
 * Corregir el folio para que diga el que Master EDI le dio de verdad.
 *
 * Karin: «la que sí hizo, solo editarla para que coincida y no tenga un
 * desfase».
 *
 * Lo que hace que esto sea seguro es la simulación: antes de que nadie
 * confirme nada, el servidor dice qué lo impide y cuántas facturas
 * quedarían recorridas. Mover una es un acto chico; mover doce es otra
 * cosa, y eso hay que verlo antes de apretar, no después.
 */
function CorregirFolio({ factura, onCerrar, onHecho }: {
  factura: Prefactura;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [folio, setFolio] = useState(String(factura.folioNumero));
  const [motivo, setMotivo] = useState('');
  const [recorrer, setRecorrer] = useState(true);
  const [simulacion, setSimulacion] = useState<SimulacionDeFolio | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const numero = Number(folio);
  const valido = Number.isInteger(numero) && numero > 0 && numero !== factura.folioNumero;

  /**
   * Se consulta al servidor y no se calcula aquí.
   *
   * Lo que impide mover un folio depende de qué más hay en la serie —si
   * el destino está ocupado, si hay timbradas en medio— y eso no vive en
   * el navegador. Calcularlo aquí sería escribir la regla dos veces, y
   * dos copias de una regla de folios acaban discrepando justo el día que
   * importa.
   */
  useEffect(() => {
    if (!valido) { setSimulacion(null); return; }

    let vigente = true;
    api.get<SimulacionDeFolio>(
      `/facturacion/prefacturas/${factura.id}/renumerar/simular`,
      { folioNumero: numero },
    )
      .then((s) => { if (vigente) setSimulacion(s); })
      .catch(() => { if (vigente) setSimulacion(null); });

    return () => { vigente = false; };
  }, [factura.id, numero, valido]);

  const impedido = (simulacion?.impedimentos.length ?? 0) > 0;

  const faltantes: string[] = [];
  if (!valido) faltantes.push('escribir un folio distinto del actual');
  if (motivo.trim().length < 5) faltantes.push('escribir el motivo');

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post(`/facturacion/prefacturas/${factura.id}/renumerar`, {
        folioNumero: numero,
        recorrerLasDeArriba: recorrer,
        motivo: motivo.trim(),
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`Corregir el folio de ${factura.folio}`}
      onCerrar={onCerrar}
      pie={(
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cerrar</button>
          <button
            className="primario"
            onClick={confirmar}
            disabled={trabajando || impedido || faltantes.length > 0}
          >
            {trabajando ? 'Corrigiendo…' : 'Corregir el folio'}
          </button>
        </>
      )}
    >
      <Error error={error} />

      <p className="sub" style={{ marginTop: 0 }}>
        Para cuando Master EDI timbró esta factura con un número distinto
        del que APRISA le había apartado. Se pone aquí el que de verdad
        quedó allá, para que el UUID la encuentre cuando llegue el archivo
        de vuelta.
      </p>

      <div className="campo">
        <label htmlFor="folio-nuevo">Folio que le dio Master EDI *</label>
        <input
          id="folio-nuevo" type="number" min={1} value={folio}
          onChange={(e) => setFolio(e.target.value)}
        />
        <span className="ayuda">
          Sólo el número. La serie sigue siendo {factura.serie}.
        </span>
      </div>

      {/* Lo que el servidor dice que lo impide. Se enseña mientras se
          teclea: el error más común es escribir un folio que ya tiene
          otra factura, y descubrirlo al confirmar obliga a rehacer el
          formulario entero. */}
      {impedido && (
        <div className="aviso error">
          {simulacion?.impedimentos.map((m) => (
            <p key={m} style={{ margin: 0 }}>{m}</p>
          ))}
        </div>
      )}

      {!impedido && simulacion && simulacion.recorrido.length > 0 && (
        <div className="aviso">
          <strong>{simulacion.aviso}</strong>
          <p style={{ margin: '6px 0 0' }}>
            Pasa cuando el lote se subió completo: a partir del folio que
            Master EDI se brincó, todas las de abajo quedaron un número
            arriba. Recorrerlas las vuelve a cuadrar y le devuelve a la
            serie el número que queda libre al final.
          </p>
          <label style={{ display: 'block', marginTop: 8 }}>
            <input
              type="checkbox"
              checked={recorrer}
              onChange={(e) => setRecorrer(e.target.checked)}
            />
            {' '}Recorrer también esas
          </label>
          {recorrer && (
            <ul className="conteo" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {simulacion.recorrido.map((m) => (
                <li key={m.id}>{m.folio} → {factura.serie}-{m.a}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="campo">
        <label htmlFor="motivo-folio">Motivo *</label>
        <textarea
          id="motivo-folio" rows={3} value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Master EDI se brincó el folio anterior y timbró ésta con el que había quedado libre."
        />
        <span className="ayuda">
          Queda en la auditoría, con el folio de antes y el de ahora.
        </span>
      </div>

      <Faltantes que={faltantes} />
    </Dialogo>
  );
}
